import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  Button,
  Typography,
  TextField,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Box,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardHeader,
  CardContent,
  Collapse
} from '@material-ui/core';
import {
  Visibility,
  VisibilityOff,
  Search,
  Add,
  SaveAlt,
  MergeType as MergeIcon,
  Edit as EditIcon,
  RadioButtonUnchecked,
  BorderOuter as BorderOuterIcon,
  Delete,
  ExpandMore,
  ExpandLess,
  Streetview,
  LocationSearching,
  Lock
} from '@material-ui/icons';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';
import ApproveAddressModal from './ApproveAddressModal'

// Import restricted area components
import RestrictedAreaDialog from '../../HMI/RestrictedAreaDialog';
import RestrictedAreasSection from '../../HMI/RestrictedAreasSection';

// Import polygon types and constants
import { POLYGON_TYPES, AREA_TYPES, AREA_TYPE_LABELS } from './constants';

// Styles
const useStyles = makeStyles(theme => ({
  sideBarDetails: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden'
  },
  paddedStuff: {
    padding: '5px',
    margin: '5px'
  },
  tabContent: {
    width: '100%',
    height: '45vh',
    overflow: 'scroll'
  },
  tabContentScroll: {
    width: '100%',
    height: '45vh',
    overflow: 'scroll'
  },
  areaItemContainer: {
    height: '20vh',
    maxHeight: '40vh',
    overflowY: 'scroll',
    marginTop: theme.spacing(2),
  },
  sidebarItemContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    borderRadius: 8,
    border: '2px solid #ccc',
    marginBottom: 10,
    marginTop: 5,
    padding: theme.spacing(1),
    alignSelf: 'stretch',
    transition: 'all 0.2s ease-in-out'
  },
  selectedSidebarItem: {
    border: '2px solid blue',
    borderRadius: 8,
    backgroundColor: 'rgba(0, 0, 255, 0.05)',
  },
  visibilityToggle: {
    marginLeft: 'auto',
    marginRight: 0,
  },
  exclusionArea: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.15)',
    }
  },
  propertyLine: {
    borderColor: '#0000FF',
    backgroundColor: 'rgba(0, 0, 255, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 255, 0.1)',
    }
  },
  manualPropertyLine: {
    borderColor: '#4caf50',
    backgroundColor: 'rgba(76, 175, 80, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    }
  },
  propertyLineHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
    borderBottom: '1px solid #ccc',
  },
  propertyLinesSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
  },
  propertyLineContent: {
    maxHeight: '25vh',
    overflowY: 'auto'
  },
  saveGeofenceArea: {
    marginTop: 50,
    display: 'flex',
    alignSelf: 'stretch',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  saveArea: {
    bottom: 30,
    display: 'flex',
    alignSelf: 'stretch',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  actionButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  geofenceExplanation: {
    fontSize: '0.875rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontStyle: 'italic'
  },
  tabInfoSection: {
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
  },
  propertyCardHeader: {
    backgroundColor: '#f0f0f0',
    padding: theme.spacing(1),
  }
}));

const Sidebar = memo(({
  polygons,
  selectedPolygonIndex,
  editingPolygonIndex,
  jobSite,
  geofenceRadius,
  activeTab,
  onStartDrawing,
  onStartGeofenceDrawing,
  onOpenEditDialog,
  onToggleVisibility,
  onSaveAreas,
  onSaveGeofenceArea,
  onUpdateGeofenceRadius,
  onTabChange,
  onChangeSite,
  onSearchMCAssessor,
  calculateNetArea,
  clients,
  url,
  handleMergeAreas,
  isEditingGeofence,
  hasGeofenceCircle,
  onStartPropertyLineDrawing,
  onSelectPropertyLine,
  onTogglePropertyLineVisibility,
  onDeletePropertyLine,
  onSavePropertyLines,
  manualPropertyLines,
  selectedPropertyLineId,
  propertyLines,
  onOpenGoogleStreetView,
  onOpenMCMaps,
  onSelectPolygon,
  restrictedAreas,
  onAddRestrictedArea,
  onEditRestrictedArea,
  onDeleteRestrictedArea,
  onToggleRestrictedAreaVisibility,
  selectedRestrictedAreaId,
  onSelectRestrictedArea
}) => {
  const styles = useStyles();
  console.log("manualPropertyLines....", manualPropertyLines);
  
  // Local state
  const [selectedClient, setSelectedClient] = useState({});
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState({});
  const [totalArea, setTotalArea] = useState(0);
  const [netArea, setNetArea] = useState(0);
  const [hideAllApiPropertyLines, setHideAllApiPropertyLines] = useState(false);
  const [showApiPropertyLines, setShowApiPropertyLines] = useState(true);
  const [showManualPropertyLines, setShowManualPropertyLines] = useState(true);
  const [showModal, setShowModal] = useState(false)
  const [addressDetails, setAddressDetails] = useState({
    jobSite: null,
    originalAddress: null,
    newAddress: null
  })
  useEffect(() => {
    console.log('Selected Site changed...get APN Details??', selectedSite)
    if (selectedSite && !selectedSite?.apn) {
      fetchAPNDetails(selectedSite)
      fetchSiteDetails(selectedSite)
    }
  }, [selectedSite])

  const fetchSiteDetails = async (site) => {
    console.log('handleSearchMCAssessor...', site);
    let addy = `${site.address}, ${site.zip}`
    console.log('Now lets find the map, too...')
    let searchURL = `https://gis.mcassessor.maricopa.gov/arcgis/rest/services/AssessorCompositeLocator/GeocodeServer/findAddressCandidates?SingleLine=${encodeURIComponent(addy)}&f=json`
    console.log('Search URL', searchURL)
    const response = await axios.get(searchURL)
    console.log('search results...', response)
    if (response?.data?.candidates?.length) {
      console.log('use the first one??', response.data.candidates[0])
      const addressCandidate = response.data.candidates[0]
      console.log('encode this fucker...', addressCandidate)
      let encodedAddy = encodeURIComponent(addressCandidate.address)
      console.log('Encoded Addy', encodedAddy)
      doesThisMatch(addressCandidate.address, addy, site)
    }
  }

  const normalizeAddress = (str) => {
    return str
      ?.toLowerCase()
      .replace(/[^\w\s]|_/g, '') // remove punctuation
      .replace(/\s+/g, '');      // remove all spaces
  };

  const doesThisMatch = (mcAddy, addy, site) => {
    console.log('do they match', mcAddy, addy)
    let formattedMC = normalizeAddress(mcAddy)
    let formattedAddy = normalizeAddress(addy)
    console.log(formattedMC)
    console.log(formattedAddy)
    let theyMatch = formattedMC === formattedAddy
    if (!theyMatch) {
      console.log('Better update our jobsite fucker...', mcAddy)
      // setShowAPNInput(true)
      setAddressDetails({
        jobSite: site,
        originalAddress: addy,
        newAddress: mcAddy
      })
      setShowModal(true)
    }
  }

  const fetchAPNDetails = async (site) => {
    console.log('Fetch it', site)
    if (!site._id) return
    const resp = await axios.get(`${url}/api/customer/getAPNAndParcelInfo?id=${site._id}&includeManual=true`)
    console.log('got the parcel info...', resp)
  }
  
  // Calculate total and net areas when polygons change
  useEffect(() => {
    // Calculate total area (sum of all non-subtraction areas)
    const total = polygons
      .filter(p => p.type !== 'propertyLine' && p.type !== 'subtraction')
      .reduce((acc, polygon) => {
        // Use the area property if available, or calculate it
        const polygonArea = parseFloat(polygon.area) || 0;
        return acc + polygonArea;
      }, 0);
    
    // Calculate net area (total minus subtractions)
    const net = calculateNetArea ? 
      polygons
        .filter(p => p.type !== 'propertyLine')
        .reduce((acc, polygon) => {
          if (polygon.type === 'subtraction') return acc;
          return acc + (calculateNetArea(polygon) || 0);
        }, 0) : 
      total;
    
    setTotalArea(total);
    setNetArea(net);
  }, [polygons, calculateNetArea]);
  
  // Get area type label
  const getAreaTypeLabel = useCallback((areaType, polygonType) => {
    // Prefer areaType if available, fall back to polygonType for legacy data
    const type = areaType || polygonType;
    return AREA_TYPE_LABELS[type] || 'Unknown';
  }, []);
  
  // Update client and site when job site changes
  useEffect(() => {
    console.log('241 sidebar jobSite or Clients changed...', jobSite, clients)
    const jobSiteClient = jobSite?.client;
    if (jobSiteClient) {
      let clientInList = clients?.find(client => {
        return (client._id === jobSiteClient._id);
      });
      
      if (clientInList) {
        setSelectedClient(clientInList);
      } else {
        setSelectedClient(jobSite?.client || {});
      }
      
      setSelectedSite(jobSite || {});
    }
  }, [jobSite, clients]);

  useEffect(() => {
    console.log('selectedClient', selectedClient)
    if (selectedClient?._id) fetchClientSites(selectedClient._id)
  }, [selectedClient])

  
  const fetchClientSites = async (_id) => {
    console.log('fetchClientSites...', _id)
    console.log('Available sits...', availableSites)
    if (availableSites.length) {
      let sitesClient = availableSites[0]['client']
      console.log('SitesClient', sitesClient)
      if (sitesClient === _id || sitesClient?._id === _id) {
        console.log('Dont bother relaoding...')
        return
      }
    }

    try {
      const response = await axios.get(`${url}/api/customer/getAllJobsites?id=${_id}&sitesOnly=true`);
      const clientJobsites = response.data;
      
      if (clientJobsites?.length) {
        setAvailableSites(clientJobsites);
        const currentSiteId = jobSite?._id
        console.log('Current Site Id', currentSiteId)
        const siteIndex = clientJobsites.findIndex(site => (site._id === currentSiteId))
        if (siteIndex > -1) {
          console.log('Same client leave it alone...')
        } else {
          console.log('Update our jobSite!!!')
          updateJobsite(null, clientJobsites[0]);
        }
      } else {
        setAvailableSites([]);
      }
    } catch (error) {
      console.error('Error fetching job sites:', error);
    }
  }

  // Handle click on polygon in sidebar
  const handleClick = useCallback((index) => {
    const polygon = polygons[index];
    if (polygon && onSelectPolygon) {
      // Call the parent's selection handler with the polygon object
      onSelectPolygon(polygon);
    }
  }, [polygons, onSelectPolygon]);
  
  const handleToggleVisibility = useCallback((event, index) => {
    event.stopPropagation(); // Prevent click from selecting the polygon
    onToggleVisibility(index);
  }, [onToggleVisibility]);
  
  // Update customer
  const updateCustomer = useCallback(async (e, newClient) => {
    setSelectedSite({});
    
    if (!newClient || !newClient._id) {
      setSelectedClient({});
      setAvailableSites([]);
      setSelectedSite({});
      onChangeSite(null);
    } else {
      setSelectedClient(newClient);
      fetchClientSites(newClient?._id)
    }
  }, [url, onChangeSite]);
  
  
  // Update job site
  const updateJobsite = useCallback(async (e, newSite) => {
    setSelectedSite(newSite);
    onChangeSite(newSite);
  }, [onChangeSite]);
  
  // Toggle visibility for all API property lines
  const handleToggleAllApiPropertyLines = useCallback(() => {
    setHideAllApiPropertyLines(!hideAllApiPropertyLines);
    
    // If we're showing API property lines now, we need to update their visibility in the polygon array
    const apiPropertyLines = polygons.filter(p => p.type === 'propertyLine');
    
    if (apiPropertyLines.length > 0) {
      apiPropertyLines.forEach(line => {
        const index = polygons.findIndex(p => p.id === line.id);
        if (index !== -1) {
          // Only toggle if the current state doesn't match what we want
          if (line.isHidden !== !hideAllApiPropertyLines) {
            onTogglePropertyLineVisibility({ stopPropagation: () => {} }, index);
          }
        }
      });
    }
  }, [hideAllApiPropertyLines, polygons, onToggleVisibility]);
  
  // Toggle visibility for sections
  const handleToggleApiSection = () => {
    setShowApiPropertyLines(!showApiPropertyLines);
  };
  
  const handleToggleManualSection = () => {
    setShowManualPropertyLines(!showManualPropertyLines);
  };
  
  // Enhanced PropertyLinesSection component
  const ApiPropertyLinesSection = useCallback(({ propertyLines }) => {
    // Always render the section, even if there are no property lines
    return (
      <Card className={styles.apiPropertyLinesSection}>
        <CardHeader
          title={
            <div className={styles.propertyLineHeader} onClick={handleToggleApiSection}>
              <Typography variant="subtitle2">
                API Property Lines {!showApiPropertyLines ? <ExpandMore /> : <ExpandLess />}
              </Typography>
              <div className={styles.masterToggleContainer}>
                <Typography variant="body2">
                  {hideAllApiPropertyLines ? "Show All" : "Hide All"}
                </Typography>
                <Switch
                  checked={hideAllApiPropertyLines}
                  onChange={handleToggleAllApiPropertyLines}
                  color="primary"
                  size="small"
                />
              </div>
            </div>
          }
          className={styles.propertyCardHeader}
        />
        <Collapse in={showApiPropertyLines}>
          <CardContent className={styles.propertyLineContent}>
            {propertyLines && propertyLines.length > 0 ? (
              propertyLines.map((line, index) => {
                // Find the actual index in the main polygons array
                const mainIndex = polygons.findIndex(p => p.id === line.id);
                
                return (
                  <div key={`property-${line.id || index}`} className={`${styles.sidebarItemContainer} ${styles.propertyLine}`}>
                    <div>
                      <div><strong>APN: {line.APN || 'N/A'}</strong></div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>API Property Line</div>
                    </div>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<Visibility />}
                          checkedIcon={<VisibilityOff />}
                          checked={hideAllApiPropertyLines || line.isHidden}
                          onChange={(e) => handleToggleVisibility(e, mainIndex)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={hideAllApiPropertyLines}
                        />
                      }
                      label=""
                      className={styles.visibilityToggle}
                    />
                  </div>
                );
              })
            ) : (
              <Typography variant="body2" style={{ padding: '8px', fontStyle: 'italic' }}>
                No API property lines available
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  }, [polygons, styles, handleToggleVisibility, hideAllApiPropertyLines, showApiPropertyLines]);
  
  // Manual Property Lines Section
  const ManualPropertyLinesSection = useCallback(() => {
    // Debug logging to see what's actually in manualPropertyLines
    console.log("Rendering ManualPropertyLinesSection with:", manualPropertyLines);
    
    // Always render the section container, even if empty
    return (
      <Card className={styles.manualPropertyLinesSection}>
        <CardHeader
          title={
            <div className={styles.propertyLineHeader} onClick={handleToggleManualSection}>
              <Typography variant="subtitle2">
                Manual Property Lines {!showManualPropertyLines ? <ExpandMore /> : <ExpandLess />}
              </Typography>
            </div>
          }
          className={styles.propertyCardHeader}
        />
        <Collapse in={showManualPropertyLines}>
          <CardContent className={styles.propertyLineContent}>
            {Array.isArray(manualPropertyLines) && manualPropertyLines.length > 0 ? (
              manualPropertyLines.map((line, index) => (
                <div 
                  key={`manual-prop-${line._id || line.id || index}`} 
                  className={`${styles.sidebarItemContainer} ${styles.manualPropertyLine} ${
                    selectedPropertyLineId === line._id ? styles.selectedSidebarItem : ''
                  }`}
                  onClick={() => onSelectPropertyLine(line._id || line.id)}
                >
                  <div>
                    <div><strong>{line.name || `Property Line ${index + 1}`}</strong></div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>Manual Boundary</div>
                  </div>
                  
                  <Box display="flex">
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePropertyLineVisibility(line.id);
                      }}
                    >
                      {line.isHidden ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePropertyLine(line._id || line.id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </div>
              ))
            ) : (
              <Typography variant="body2" style={{ padding: '8px', fontStyle: 'italic' }}>
                No manual property lines defined
              </Typography>
            )}
          </CardContent>
        </Collapse>
      </Card>
    );
  }, [
    manualPropertyLines, 
    styles, 
    selectedPropertyLineId, 
    onSelectPropertyLine, 
    onTogglePropertyLineVisibility, 
    onDeletePropertyLine,
    showManualPropertyLines
  ]);
  
  const onMergeAreas = () => {
    console.log('Merge areas...')
    handleMergeAreas()
  }


    const handleApprove = async () => {
      console.log("User approved address update!");
      setShowModal(false);
      try {
        const obj = {
          jobSiteId: jobSite?._id,
          addressDetails
        }
        const resp = await axios.post(`${url}/api/customer/updateJobSite?addressOnly=true`, obj)
        console.log('resp...', resp)
        if (resp?.updatedSite?._id) {
          console.log('updated our site...', resp.updatedSite)
        }
      } catch (e) {
        console.log('Error updating jobsite address...', e)
      }
    };

    const handleCancel = () => {
      console.log("User cancelled address update.");
      setShowModal(false);
    };

  
  return (
    <div className={styles.sideBarDetails}>
      <div>
        <Autocomplete
          id="customer"
          options={clients || []}
          getOptionLabel={(option) => {
            let displayname = `${option?.displayName ? option?.displayName : option?.searchName ? `${option.searchName}` : ''}`;
            return displayname;
          }}
          className={styles.paddedStuff}
          onChange={updateCustomer}
          fullWidth
          selectOnFocus
          value={selectedClient}
          renderInput={(params) => <TextField variant="outlined" {...params} label="Customer" />}
        />
        
        <Autocomplete
          id="jobsite"
          options={availableSites || []}
          getOptionLabel={(site) => {
            let siteInfo = `${site?.name || ''} ${site?.address || ''} ${site?.buildingNumber || ''}`;
            return siteInfo;
          }}
          className={styles.paddedStuff}
          onChange={updateJobsite}
          fullWidth
          selectOnFocus
          value={selectedSite}
          renderInput={(params) => <TextField variant="outlined" {...params} label="Jobsite" />}
        />
      </div>
      
      {selectedSite && selectedSite._id && (
        <Paper elevation={2} style={{ padding: '10px', margin: '10px 0', backgroundColor: '#f8f8f8' }}>
          <div>
            {selectedClient && selectedClient._id && (
              <Typography variant="subtitle1">{selectedClient.displayName}</Typography>
            )}
            <Typography variant="body2">
              {selectedSite?.areas?.length ? `(${selectedSite.areas.length} Areas Mapped)` : ''}
            </Typography>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" style={{ maxWidth: '80%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedSite.name} {selectedSite?.address}
            </Typography>
            
            <Tooltip title="Search MC Assessor (Find Valid Address/APN)">
              <IconButton onClick={onSearchMCAssessor} size="small">
                <LocationSearching />
              </IconButton>
            </Tooltip>
            <Tooltip title="Search MC Assessor (Higher-Res Images)">
              <IconButton onClick={onOpenMCMaps} size="small">
                <Search />
              </IconButton>
            </Tooltip>
            <Tooltip title="View Google Street View">
              <IconButton onClick={onOpenGoogleStreetView} size="small">
                <Streetview />
              </IconButton>
            </Tooltip>
          </div>
        </Paper>
      )}
      
      <Tabs 
        value={activeTab} 
        onChange={onTabChange}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        style={{ maxWidth: '100%' }}
      >
        <Tab label="Measure Property" />
        <Tab label="Draw Areas" />
        <Tab label="Property Lines" />
      </Tabs>

      {activeTab === 0 && (
        <div className={styles.tabContentScroll}>
          <div className={styles.tabInfoSection}>
            <Typography variant="subtitle2">
              Geofence Setup
            </Typography>
            <Typography variant="body2" className={styles.geofenceExplanation}>
              Define a geofence radius around this property to establish a service boundary.
              {hasGeofenceCircle && (
                <span> You can <strong>drag the circle</strong> to reposition it or adjust the radius below.</span>
              )}
            </Typography>
          </div>
          
          {!hasGeofenceCircle && (
            <Button 
              variant="contained" 
              color="primary" 
              onClick={onStartGeofenceDrawing}
              startIcon={<RadioButtonUnchecked />}
              fullWidth
              className={styles.actionButton}
            >
              Draw Geofence Circle
            </Button>
          )}
          
          {hasGeofenceCircle && !isEditingGeofence && (
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={onStartGeofenceDrawing}
              startIcon={<EditIcon />}
              fullWidth
              className={styles.actionButton}
            >
              Edit Geofence Circle
            </Button>
          )}
          
          {hasGeofenceCircle && isEditingGeofence && (
            <Typography variant="body2" style={{
              padding: '8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px',
              marginBottom: '16px',
              border: '1px solid #bbdefb'
            }}>
              <strong>Editing Geofence:</strong> Drag circle to reposition, drag edge to resize
            </Typography>
          )}
          
          <div className={styles.saveGeofenceArea}>
            <Button 
              variant="contained" 
              color={isEditingGeofence ? "secondary" : "primary"}
              onClick={onSaveGeofenceArea}
              startIcon={<SaveAlt />}
              fullWidth
              disabled={!hasGeofenceCircle}
              style={isEditingGeofence ? { backgroundColor: '#4caf50', fontWeight: 'bold' } : {}}
            >
              Save Geofence Location & Radius
            </Button>
            
            {hasGeofenceCircle && (
              <Typography variant="caption" color="textSecondary" style={{ textAlign: 'center', marginTop: '8px' }}>
                This will update the jobsite location and service radius
              </Typography>
            )}
          </div>
        </div>
      )}

      {activeTab === 1 && (
        <div className={styles.tabContent}>
          <div className={styles.tabInfoSection}>
            <Typography variant="subtitle2">
              Area Drawing
            </Typography>
            <Typography variant="body2" className={styles.geofenceExplanation}>
              Draw and define specific areas within the property for maintenance tracking.
            </Typography>
          </div>
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={onStartDrawing}
            startIcon={<Add />}
            fullWidth
            className={styles.actionButton}
          >
            Create New Area
          </Button>
          
          {polygons.filter(p => p.type !== 'propertyLine').length > 0 && (
              <Paper elevation={1} style={{ padding: '8px', margin: '16px 0', backgroundColor: '#accacd' }}>
              <Typography variant="subtitle2">Areas Summary</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={onMergeAreas}
                disabled={polygons.filter(p => p.type !== 'propertyLine').length < 2}
                startIcon={<MergeIcon />}
                size="small"
                style={{ marginTop: '8px', marginBottom: '8px' }}
              >
                Merge Areas
              </Button>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Area:</Typography>
                <Typography variant="body2">{totalArea.toFixed(2)} sq ft</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Net Area:</Typography>
                <Typography variant="body2">{netArea.toFixed(2)} sq ft</Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Perimeter:</Typography>
                <Typography variant="body2">
                  {polygons
                    .filter(p => p.type !== 'propertyLine' && p.type !== 'subtraction')
                    .reduce((sum, item) => sum + (parseFloat(item.perimeter) || 0), 0)
                    .toFixed(2)} lin ft
                </Typography>
              </div>
            </Paper>
          )}
              
          <div className={styles.areaItemContainer}>
            {polygons
              .filter(item => item.type !== 'propertyLine')
              .map((item, index) => {
                const mainIndex = polygons.findIndex(p => p._id === item._id);
                
                const areaFeet = parseFloat(item.area) || 0;
                const perimeter = parseFloat(item.perimeter) || 0

                const itemColorBlock = {
                  height: 30,
                  width: 30,
                  borderRadius: 5,
                  backgroundColor: item.fillColor,
                  border: `2px solid ${item.strokeColor}`
                };
                
                const isExclusionArea = item?.type === 'subtraction';
                const isDoNotArea = item?.areaType === AREA_TYPES.DO_NOT_SPRAY || item?.areaType === AREA_TYPES.DO_NOT_SERVICE;
                
                const itemStyles = `${styles.sidebarItemContainer} ${
                  mainIndex === selectedPolygonIndex ? styles.selectedSidebarItem : ''
                } ${isExclusionArea ? styles.exclusionArea : ''} ${isDoNotArea ? styles.exclusionArea : ''}`;
                
                return (
                  <div key={item._id || index} className={itemStyles} onClick={() => handleClick(mainIndex)}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography 
                          variant="subtitle2" 
                          style={{ 
                            textOverflow: 'ellipsis', 
                            overflow: 'hidden', 
                            whiteSpace: 'nowrap',
                            maxWidth: '70%'
                          }}
                        >
                          {item.name}
                        </Typography>
                        <div style={itemColorBlock}></div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" style={{ color: '#666' }}>
                          {getAreaTypeLabel(item.areaType, item.polygonType)}
                        </Typography>
                        <Typography variant="body2">
                        {areaFeet.toFixed(2)} SF | {perimeter.toFixed(2)} LF
                        </Typography>
                      </div>
                    </div>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<Visibility />}
                          checkedIcon={<VisibilityOff />}
                          checked={item.isHidden}
                          onChange={(e) => handleToggleVisibility(e, mainIndex)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label=""
                      className={styles.visibilityToggle}
                    />
                  </div>
                );
              })
            }
          </div>
          
          {activeTab === 1 && polygons?.length > 0 && (
            <div className={styles.saveArea}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={onSaveAreas}
                startIcon={<SaveAlt />}
                fullWidth
              >
                Save Areas
              </Button>
            </div>
          )}
        </div>
      )}

      {activeTab === 2 && (
        <div className={styles.tabContent}>
          <div className={styles.tabInfoSection}>
            <Typography variant="subtitle2">
              Property Lines
            </Typography>
            <Typography variant="body2" className={styles.geofenceExplanation}>
              View and manage property boundaries. You can hide any property line that interferes with your areas.
              {manualPropertyLines?.length > 0 && (
                <span> Manually drawn property lines can be edited or deleted.</span>
              )}
            </Typography>
          </div>
          
          {/* Button to draw new property line */}
          <Button
            variant="contained"
            color="primary"
            onClick={onStartPropertyLineDrawing}
            startIcon={<BorderOuterIcon />}
            fullWidth
            className={styles.actionButton}
          >
            Draw New Property Boundary
          </Button>

          {/* Restricted Areas Section */}
          <RestrictedAreasSection
            restrictedAreas={restrictedAreas}
            onAddArea={onAddRestrictedArea}
            onEditArea={onEditRestrictedArea}
            onDeleteArea={onDeleteRestrictedArea}
            onToggleVisibility={onToggleRestrictedAreaVisibility}
            selectedAreaId={selectedRestrictedAreaId}
            onSelectArea={onSelectRestrictedArea}
            isDrawingMode={false}
            jobSite={selectedSite}
            client={selectedClient}
          />
          
          {/* Unified Property Lines List */}
          <Card className={styles.propertyLinesSection}>
            <CardHeader
              title={
                <div className={styles.propertyLineHeader}>
                  <Typography variant="subtitle2">Property Lines</Typography>
                </div>
              }
              className={styles.propertyCardHeader}
            />
            <CardContent className={styles.propertyLineContent}>
              {/* API Property Lines */}
              {propertyLines && propertyLines.map((line, index) => {
                return (
                  <div key={`property-${line.id || line._id || index}`} className={`${styles.sidebarItemContainer} ${styles.propertyLine}`}>
                    <div>
                      <div><strong>APN: {line.APN || 'N/A'}</strong></div>
                      <div style={{ fontSize: '0.8em', color: '#666' }}>County Property Line</div>
                    </div>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          icon={<Visibility />}
                          checkedIcon={<VisibilityOff />}
                          checked={line.isHidden}
                          // onChange={(e) => onTogglePropertyLineVisibility({ stopPropagation: () => {} }, propertyLines.indexOf(line))}
                          onClick={(e) => {
                            e.stopPropagation()
                            onTogglePropertyLineVisibility(line.id)
                          }}
                        />
                      }
                      label=""
                      className={styles.visibilityToggle}
                    />
                  </div>
                );
              })}
              
              {/* Manual Property Lines */}
              {Array.isArray(manualPropertyLines) && manualPropertyLines.map((line, index) => (
                <div 
                  key={`manual-prop-${line._id || line.id || index}`} 
                  className={`${styles.sidebarItemContainer} ${styles.manualPropertyLine} ${
                    selectedPropertyLineId === line._id ? styles.selectedSidebarItem : ''
                  }`}
                  onClick={() => onSelectPropertyLine(line._id || line.id)}
                >
                  <div>
                    <div><strong>{line.name || `Custom Boundary ${index + 1}`}</strong></div>
                    <div style={{ fontSize: '0.8em', color: '#666' }}>Manual Property Line</div>
                  </div>
                  
                  <Box display="flex">
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePropertyLineVisibility(line._id || line.id);
                      }}
                    >
                      {line.isHidden ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePropertyLine(line._id || line.id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </div>
              ))}
              
              {/* Empty state message */}
              {(!propertyLines?.length && !manualPropertyLines?.length) && (
                <Typography variant="body2" style={{ padding: '8px', fontStyle: 'italic' }}>
                  No property lines available. Use the button above to draw a property boundary.
                </Typography>
              )}
            </CardContent>
          </Card>
          
          {/* Save button for manual property lines */}
          {Array.isArray(manualPropertyLines) && manualPropertyLines.length > 0 && (
            <div className={styles.saveArea}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={onSavePropertyLines}
                startIcon={<SaveAlt />}
                fullWidth
              >
                Save Property Boundaries
              </Button>
            </div>
          )}
        </div>
      )}

      <ApproveAddressModal
        open={showModal}
        addressDetails={addressDetails}
        onApprove={handleApprove}
        onCancel={handleCancel}
      />

    </div>
  );
});

// Set display name for debugging
Sidebar.displayName = 'Sidebar';

export default Sidebar;
